import React from 'react';
import { StepName } from '|/app/services';
import { useBrand, useBrandCapabilities, useBrandCopy } from '|/brand';
import LinkButton from '|/components/carvana-ds/link-button';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import useNewCar from '|/hooks/use-new-car';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import deliveryLocationImage from './assets/delivery-location.jpg';
import pickupLocationImage from './assets/pickup-location.jpg';
import slateDeliveryLocationImage from './assets/slate-delivery-location.jpg';
import styles from './getting-vehicle.module.css';
import useOrderSummaryVm from '../../use-order-summary-vm';

interface GettingVehicleProps {
  summary?: ReviewSummaryResponse;
  variant?: string;
  testId?: string;
  vehicleType?: string;
}

const GettingVehicle = ({
  summary = {} as ReviewSummaryResponse,
  variant = '',
  testId = 'getting-vehicle-section',
  vehicleType: vehicleTypeProp
}: GettingVehicleProps) => {
  const navigateToStep = useNavigateToStep();
  const { getDealershipDisplayName } = useNewCar();
  const { brand } = useBrand();
  const { checkout } = useBrandCapabilities();
  const copy = useBrandCopy();
  const {
    hasTransferMethod,
    hasTransferMethod: showEdit,
    isVendingMachine,
    isDelivery,
    isPickup,
    transferAddress,
    accessoryAddresses,
    hasAccessoryAddress,
    defaultAccessoryShippingType,
    vehicleType: vehicleTypeFromVm
  } = useOrderSummaryVm(summary);

  if (!hasTransferMethod) return null;

  const vehicleType = vehicleTypeProp ?? vehicleTypeFromVm;

  const isNewCarDealership = vehicleType?.toLowerCase() === 'new';

  const deliveryLabel = copy.deliveryLabel;
  const addressLabel = isDelivery ? deliveryLabel : 'Picking up from';
  const deliveryImage = brand === 'slate' ? slateDeliveryLocationImage : deliveryLocationImage;
  const locationImage = isDelivery ? deliveryImage : pickupLocationImage;
  const locationNameDisplay = isVendingMachine
    ? `${transferAddress?.city} Vending Machine`
    : isNewCarDealership
      ? getDealershipDisplayName(transferAddress?.city ?? '', transferAddress?.city)
      : 'Carvana pickup location';

  const accessoryShippingAddress = accessoryAddresses[defaultAccessoryShippingType];

  return (
    <section data-testid={testId}>
      <h3>{copy.gettingVehicleHeading}</h3>

      {hasTransferMethod && transferAddress && (
        <div className={styles.addressSection}>
          {variant !== 'sidebar' && (
            <img src={locationImage} alt={addressLabel} className={styles.addressImage} />
          )}
          <div className={styles.addressContent}>
            <h4 className={styles.addressLabel}>{addressLabel}</h4>
            <div className={styles.addressText}>
              {isPickup && <p className={styles.locationName}>{locationNameDisplay}</p>}
              {transferAddress.line1} {transferAddress.line2 && transferAddress.line2}
              <br />
              {transferAddress.city}, {transferAddress.state} {transferAddress.zipCode}
            </div>
          </div>
        </div>
      )}

      <div className={styles.schedulingMessage}>
        You&apos;ll pick the specific date and time for your {isDelivery ? 'delivery' : 'pickup'}{' '}
        after completing verification.
      </div>

      {showEdit && (
        <LinkButton
          className={styles.editButton}
          size="sm"
          underline={false}
          onClick={() => {
            navigateToStep(StepName.PickupDelivery);
          }}
          data-analytics-id="pp_review_order_summary_edit_pickup_delivery"
          data-testid="getting-vehicle-edit-button"
        >
          Edit {isDelivery ? 'delivery' : 'pickup'}
        </LinkButton>
      )}

      {checkout.sellsAccessories && hasAccessoryAddress && (
        <div className={styles.accessoriesSection}>
          <div className={styles.addressContent}>
            <h4 className={styles.addressLabel}>Self-installed options shipping to</h4>
            {accessoryShippingAddress && (
              <div className={styles.addressText}>
                {accessoryShippingAddress.address.street}{' '}
                {accessoryShippingAddress.address.street2 &&
                  accessoryShippingAddress.address.street2}
                <br />
                {accessoryShippingAddress.address.city}, {accessoryShippingAddress.address.state}{' '}
                {accessoryShippingAddress.address.zip5}
              </div>
            )}
          </div>
          <LinkButton
            className={styles.editButton}
            size="sm"
            underline={false}
            onClick={() => {
              navigateToStep(StepName.Accessories);
            }}
            data-analytics-id="pp_review_order_summary_edit_accessories_shipping_address"
            data-testid="getting-vehicle-edit-accessories-shipping-button"
          >
            Edit self-installed options shipping address
          </LinkButton>
        </div>
      )}
    </section>
  );
};

export default GettingVehicle;
